import { Controller } from "@hotwired/stimulus"

// Shows one section of a long form at a time. The URL hash picks the section:
// either a panel's own id (#section-pricing) or the id of anything inside it
// (#portfolio_pricing, used by the portfolio overview's Edit links), so deep
// links and reloads land on the right section.
export default class extends Controller {
  static targets = ["link", "panel"]

  connect() {
    this.show(this.panelFor(this.hashId()) || this.panelTargets[0])
    this.onHashChange = () => {
      const panel = this.panelFor(this.hashId())
      if (panel) this.show(panel)
    }
    window.addEventListener("hashchange", this.onHashChange)
  }

  disconnect() {
    window.removeEventListener("hashchange", this.onHashChange)
  }

  select(event) {
    event.preventDefault()
    const id = event.currentTarget.hash.slice(1)
    history.replaceState(history.state, "", `#${id}`)
    this.show(this.panelFor(id), { focus: true })
  }

  // The nav is re-rendered by turbo-streams after each save; keep the
  // current section marked on the fresh links.
  linkTargetConnected() {
    if (this.current) this.markLinks()
  }

  show(panel, { focus = false } = {}) {
    if (!panel) return
    this.current = panel
    this.panelTargets.forEach((p) => { p.hidden = p !== panel })
    this.markLinks()

    if (focus) {
      // Move focus to the section heading so keyboard and screen-reader users
      // land in the content they just opened.
      const heading = panel.querySelector("h2")
      if (heading) {
        heading.tabIndex = -1
        heading.focus({ preventScroll: true })
      }
      if (panel.getBoundingClientRect().top < 0) panel.scrollIntoView({ block: "start" })
    }
  }

  markLinks() {
    this.linkTargets.forEach((link) => {
      if (link.hash.slice(1) === this.current.id) {
        link.setAttribute("aria-current", "true")
      } else {
        link.removeAttribute("aria-current")
      }
    })
  }

  panelFor(id) {
    if (!id) return null
    return this.panelTargets.find((p) => p.id === id || p.querySelector(`#${CSS.escape(id)}`))
  }

  hashId() {
    return decodeURIComponent(window.location.hash.slice(1))
  }
}
